import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { TotalSaleGroupByDropdown } from "./TotalSaleGroupedBarCard";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mapTopProductsApiToRows } from "./dashboardChartsModel";
import DashboardChartCardSkeleton from "./DashboardChartCardSkeleton";
import { _Api } from "../common/api/_call";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";

const TOP_LIMITS = [10, 20, 30];
/** In-flight dedupe for dev StrictMode double-mount. */
const TOP_PRODUCTS_IN_FLIGHT = new Map();

function formatTableAmount(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.round(v));
}

/**
 * Row count selector — same visual language as “By Amounts” (anchored under trigger).
 * @param {{ value: number; onChange?: (v: number) => void }} props
 */
function TopLimitDropdown({ value, onChange }) {
  const { t } = useTranslation();
  const menuId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const pick = (n) => {
    if (TOP_LIMITS.includes(n)) onChange?.(n);
    close();
  };

  return (
    <div className="pos-dashboard-chart-card__groupby pos-dashboard-chart-card__groupby--top-limit" ref={wrapRef}>
      <button
        type="button"
        className="pos-dashboard-chart-card__groupby-trigger pos-dashboard-chart-card__groupby-trigger--compact"
        aria-label={t("Rows to show", { defaultValue: "Rows to show" })}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pos-dashboard-chart-card__groupby-trigger-text">{value}</span>
        <span className="pos-dashboard-chart-card__groupby-chevron" aria-hidden>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="#8A8A8A" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open ? (
        <div
          id={menuId}
          className="pos-dashboard-chart-card__groupby-menu pos-dashboard-chart-card__groupby-menu--limit"
          role="listbox"
          aria-label={t("Rows to show", { defaultValue: "Rows to show" })}
        >
          <div className="pos-dashboard-chart-card__groupby-menu-inner">
            {TOP_LIMITS.map((n) => (
              <button
                key={n}
                type="button"
                role="option"
                aria-selected={value === n}
                className={`pos-dashboard-chart-card__groupby-option ${value === n ? "pos-dashboard-chart-card__groupby-option--selected" : ""}`}
                onClick={() => pick(n)}
              >
                <span className="pos-dashboard-chart-card__groupby-option-row">{n}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<{ key: string; label: string; collection: string; amount: number; pct: number }>} props.rows
 * @param {{ key: string; dir: 'asc' | 'desc' }} props.sort
 * @param {(key: string) => void} props.onToggleSort
 */
function TopProductsDataTable({ rows, sort, onToggleSort }) {
  const { t } = useTranslation();

  return (
    <div className="pos-dashboard-chart-table pos-dashboard-chart-table--top-products" role="table" aria-label={t("Top products", { defaultValue: "Top products" })}>
      <div className="pos-dashboard-chart-table__thead" role="rowgroup">
        <div className="pos-dashboard-chart-table__tr pos-dashboard-chart-table__tr--head pos-dashboard-chart-table__tr--top-products-head" role="row">
          <div className="pos-dashboard-chart-table__th pos-dashboard-chart-table__th--idx pos-dashboard-chart-table__th--first" role="columnheader">
            #
          </div>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--product"
            ariaSortLabel={t("Sort by product", { defaultValue: "Sort by product" })}
            sorted={sort.key === "name"}
            direction={sort.dir}
            onSort={() => onToggleSort("name")}
          >
            {t("Product", { defaultValue: "Product" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--item"
            ariaSortLabel={t("Sort by item", { defaultValue: "Sort by item" })}
            sorted={sort.key === "item"}
            direction={sort.dir}
            onSort={() => onToggleSort("item")}
          >
            {t("Item", { defaultValue: "Item" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--collection"
            ariaSortLabel={t("Sort by collection", { defaultValue: "Sort by collection" })}
            sorted={sort.key === "collection"}
            direction={sort.dir}
            onSort={() => onToggleSort("collection")}
          >
            {t("Collection", { defaultValue: "Collection" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--num pos-dashboard-chart-table__th--qty"
            ariaSortLabel={t("Sort by quantity", { defaultValue: "Sort by quantity" })}
            sorted={sort.key === "qty"}
            direction={sort.dir}
            onSort={() => onToggleSort("qty")}
          >
            {t("Qty", { defaultValue: "Qty" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--last pos-dashboard-chart-table__th--num pos-dashboard-chart-table__th--amount"
            ariaSortLabel={t("Sort by amount", { defaultValue: "Sort by amount" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => onToggleSort("amount")}
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-dashboard-chart-table__scroll" role="rowgroup">
        {rows.map((row, i) => (
          <div key={`${row.sku}-${row.name}-${i}`} className="pos-dashboard-chart-table__tr pos-dashboard-chart-table__tr--top-product" role="row">
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--idx" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{i + 1}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--product" role="cell">
              <span className="pos-dashboard-top-product__product-cell">
                {row.mainImage ? (
                  <img
                    className="pos-dashboard-top-product__thumb pos-dashboard-top-product__thumb-img"
                    src={row.mainImage}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <span className="pos-dashboard-top-product__thumb" aria-hidden />
                )}
                <span className="pos-dashboard-top-product__product-text">
                  <span className="pos-dashboard-top-product__sku">{row.sku}</span>
                  <span className="pos-dashboard-top-product__name">{row.name}</span>
                </span>
              </span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--item" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{row.item}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--collection" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{row.collection}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--num" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">
                {row.qty}
              </span>
              <span style={{fontSize:'12px',color:'#A4A4A4'}} className="pos-dashboard-chart-table__cell-inner" >({row.qtySharePct}%)</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--amount" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableAmount(row.amount)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Top Products — table-only card; header: Top + limit + Products, By Amounts, kebab (Download / Remove).
 * Place on dashboard chart grid row 2, left column.
 *
 * @param {object} props
 * @param {Date | null} [props.startDate]
 * @param {Date | null} [props.endDate]
 * @param {() => void} [props.onMenuDownload]
 * @param {() => void} [props.onMenuRemove]
 */
function TopProductsChartCard({ startDate, endDate, onMenuDownload, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [topLimit, setTopLimit] = useState(10);
  const [groupBy, setGroupBy] = useState(() => "amount");
  const [sort, setSort] = useState(() => ({ key: "amount", dir: /** @type {'asc'|'desc'} */ ("desc") }));
  /** `null` = not loaded yet; array = API result (may be empty). Never show static demo rows. */
  const [apiRows, setApiRows] = useState(/** @type {null | import("./dashboardChartsModel.js").TopProductTableRow[]} */ (null));
  const [fetchLoading, setFetchLoading] = useState(true);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("top-products"));
    onMenuDownload?.();
  }, [onMenuDownload]);

  useEffect(() => {
    setSort({ key: groupBy === "quantity" ? "qty" : "amount", dir: "desc" });
  }, [groupBy]);

  useEffect(() => {
    let active = true;
    setFetchLoading(true);
    const fetchTopProducts = async () => {
      try {
        if (!startDate) {
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
          limit: topLimit,
        };
        const reqKey = `${payload.start_date}|${payload.end_date}|${payload.limit}`;
        const existing = TOP_PRODUCTS_IN_FLIGHT.get(reqKey);
        const req =
          existing ??
          _Api(payload, "api/v1/POS/dashboard/top-products").finally(() => {
            TOP_PRODUCTS_IN_FLIGHT.delete(reqKey);
          });
        if (!existing) {
          TOP_PRODUCTS_IN_FLIGHT.set(reqKey, req);
        }
        const res = await req;
        if (!active) return;
        if (res?.code === 200) {
          setApiRows(mapTopProductsApiToRows(res));
        } else {
          setApiRows([]);
        }
      } catch {
        if (active) setApiRows([]);
      } finally {
        if (active) setFetchLoading(false);
      }
    };
    fetchTopProducts();
    return () => {
      active = false;
    };
  }, [startDate, endDate, topLimit]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const rows = useMemo(() => {
    const base = apiRows != null ? apiRows.map((r) => ({ ...r })) : [];
    const mul = sort.dir === "asc" ? 1 : -1;
    base.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "name":
          c = String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" });
          break;
        case "item":
          c = String(a.item).localeCompare(String(b.item), undefined, { sensitivity: "base" });
          break;
        case "collection":
          c = String(a.collection).localeCompare(String(b.collection), undefined, { sensitivity: "base" });
          break;
        case "qty":
          c = a.qty - b.qty;
          break;
        case "amount":
          c = a.amount - b.amount;
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return base;
  }, [sort, topLimit, apiRows]);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--top-products">
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--with-controls">
        <div className="pos-dashboard-chart-card__title-cluster">
          <span className="pos-dashboard-chart-card__title-prefix">{t("Top", { defaultValue: "Top" })}</span>
          <TopLimitDropdown value={topLimit} onChange={setTopLimit} />
          <h2 className="pos-dashboard-chart-card__title pos-dashboard-chart-card__title--inline">
            {t("Products", { defaultValue: "Products" })}
          </h2>
        </div>
        <div className="pos-dashboard-chart-card__header-actions">
          <TotalSaleGroupByDropdown value={groupBy === "quantity" ? "quantity" : "amount"} onChange={(v) => setGroupBy(v)} />
          <DashboardChartCardKebabMenu showViewToggle={false} onDownload={handleDownloadPng} onRemove={onMenuRemove} />
        </div>
      </header>
      <div className="pos-dashboard-chart-card__body pos-dashboard-chart-card__body--table">
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          {fetchLoading ? (
            <div className="pos-dashboard-chart-card__capture-skeleton">
              <DashboardChartCardSkeleton />
            </div>
          ) : (
            <TopProductsDataTable rows={rows} sort={sort} onToggleSort={toggleSort} />
          )}
        </div>
      </div>
    </div>
  );
}

export default TopProductsChartCard;
export { TopLimitDropdown };
